import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Buton, Kart } from "@koclukpanel/ui";
import { api, ApiHatasi } from "./api";

export interface VeliBagiSayfasi {
  ogrenciId: string;
  bagId: string;
  jetonId: string;
  jeton: string;
}

export function veliBagiSayfasiniOku(): VeliBagiSayfasi | null {
  const parcalar = window.location.hash.match(
    /^#veli-bagi-dogrula\/([A-Za-z0-9_-]{1,64})\/([A-Za-z0-9_-]{1,64})\/([A-Za-z0-9_-]{1,64})\/([A-Za-z0-9_-]{43})$/,
  );
  if (!parcalar) return null;
  return {
    ogrenciId: parcalar[1]!,
    bagId: parcalar[2]!,
    jetonId: parcalar[3]!,
    jeton: parcalar[4]!,
  };
}

function hataMetni(hata: unknown): string {
  return hata instanceof ApiHatasi ? hata.message : "İşlem tamamlanamadı. Yeniden deneyin.";
}

export function VeliBagiDogrulama({
  sayfa,
  onKapat,
}: {
  sayfa: VeliBagiSayfasi;
  onKapat: () => void;
}) {
  const sorgular = useQueryClient();
  useEffect(() => {
    // F-MNT-05 — girişten sonra sır yalnız bellekte kalır; geçmişe ve sonraki gezintilere taşınmaz.
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search,
    );
  }, []);
  const dogrula = useMutation({
    mutationFn: () =>
      api(`/ogrenciler/${sayfa.ogrenciId}/veliler/${sayfa.bagId}/dogrulama`, {
        yontem: "POST",
        govde: { jetonId: sayfa.jetonId, jeton: sayfa.jeton },
      }),
    retry: false,
    onSuccess: async () => {
      await sorgular.invalidateQueries({ queryKey: ["veli-bagi-dogrulama"] });
    },
  });
  return (
    <Kart baslik="Veli bağını doğrulama">
      {dogrula.isSuccess ? (
        <p role="status">Veli bağınız doğrulandı. Rıza işlemlerinize devam edebilirsiniz.</p>
      ) : (
        <>
          <p>Bu bağlantının gönderildiği veli hesabıyla giriş yapmış olmanız gerekir.</p>
          <p>Bağı doğrulamak rıza verdiğiniz anlamına gelmez.</p>
          <Buton onClick={() => dogrula.mutate()} yukleniyor={dogrula.isPending}>
            Veli bağımı doğrula
          </Buton>
        </>
      )}
      {dogrula.isError && (
        <div role="alert">
          <p>{hataMetni(dogrula.error)}</p>
          {/* F-MNT-05 — sır URL'den silindiği için hesap değiştirmek bağlantıyı geri getirmez. */}
          <p>
            Bağlantıyı başka bir hesapta açtıysanız, bağlantının gönderildiği veli hesabıyla giriş
            yapın ve e-postadaki doğrulama bağlantısını yeniden açın.
          </p>
        </div>
      )}
      <Buton gorunum="ikincil" onClick={onKapat}>
        Veli portalına dön
      </Buton>
    </Kart>
  );
}

export function VeliBagiDogrulamaGirisi({ ogrenciId }: { ogrenciId: string }) {
  const durum = useQuery({
    queryKey: ["veli-bagi-dogrulama", ogrenciId],
    queryFn: ({ signal }) =>
      api<{ bagId: string; verifiedAt: string | null }>(
        `/ogrenciler/${encodeURIComponent(ogrenciId)}/veliler/dogrulama/kendi`,
        { signal, taze: true },
      ),
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: "always",
  });
  const gonder = useMutation({
    mutationFn: () =>
      api(
        `/ogrenciler/${encodeURIComponent(ogrenciId)}/veliler/${durum.data!.bagId}/dogrulama/iste`,
        {
          yontem: "POST",
        },
      ),
    retry: false,
  });
  return (
    <Kart baslik="Veli bağı">
      {durum.isPending ? (
        <p>Doğrulama durumu alınıyor…</p>
      ) : durum.isError ? (
        <>
          <p role="alert">Veli bağının doğrulama durumu alınamadı.</p>
          <Buton gorunum="ikincil" onClick={() => void durum.refetch()}>
            Yeniden dene
          </Buton>
        </>
      ) : durum.data.verifiedAt ? (
        <p>Veli bağınız doğrulandı.</p>
      ) : (
        <>
          <p>
            Veli bağınızı doğrulamak isteğe bağlıdır. Mentörüm rızası ve çocuğunuzun veri paketini
            indirmek için gerekir.
          </p>
          <Buton onClick={() => gonder.mutate()} yukleniyor={gonder.isPending}>
            Bağı doğrula
          </Buton>
        </>
      )}
      {gonder.isSuccess && (
        <p role="status">
          Bağlantı kendi e-posta adresinize gönderildi. Bağlantıyı bu hesapla giriş yaparak açın.
        </p>
      )}
      {gonder.isError && <p role="alert">{hataMetni(gonder.error)}</p>}
    </Kart>
  );
}
